import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaArrowRight } from "react-icons/fa6";

gsap.registerPlugin(ScrollTrigger);

const HowWeDoItSection = () => {
    const sectionRef = useRef(null);
    const textRefs = useRef([]);
    const listRefs = useRef([]);
    const cardRefs = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 75%",
                    toggleActions: "play none none none",
                }
            });

            tl.from(textRefs.current, {
                opacity: 0,
                duration: 0.6,
                stagger: 0.1,
                ease: "power2.out"
            });

            tl.from(listRefs.current, {
                x: -30,
                opacity: 0,
                duration: 0.5,
                stagger: 0.15,
                ease: "power2.out"
            }, "-=0.3");

            tl.from(cardRefs.current, {
                opacity: 0,
                duration: 0.8,
                stagger: 0.2,
                ease: "back.out(1.7)"
            }, "-=0.5");

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    const addToTextRefs = (el) => { if (el && !textRefs.current.includes(el)) textRefs.current.push(el); };
    const addToListRefs = (el) => { if (el && !listRefs.current.includes(el)) listRefs.current.push(el); };
    const addToCardRefs = (el) => { if (el && !cardRefs.current.includes(el)) cardRefs.current.push(el); };

    return (
        // FIX 1: Added 'items-start' to the section grid container
        <section ref={sectionRef} className='container max-w-7xl mx-auto px-6 md:px-12 my-[100px] grid min-[950px]:grid-cols-[1fr_2fr] gap-14 items-start'>
            
            {/* --- LEFT COLUMN --- */}
            {/* FIX 2: Removed 'py-3' so it aligns flush to the top */}
            <div className='flex flex-col gap-y-7'>
                <h3 ref={addToTextRefs} className='text-[#000000] font-[600] text-[34px] sm:text-[40px] leading-[40px] sm:leading-[48px]'>
                    How we do it
                </h3>
                <h5 ref={addToTextRefs} className='text-[#000000] font-[600] text-[18px] leading-[30px]'>
                    We leverage AI to build MVP tech products and execute GTM plans
                </h5>
                <p ref={addToTextRefs} className='text-[#000000] text-[15px] font-[500] leading-[20px]'>
                    We’re your execution partner — not just advisors. At TurningIdeas, we help you build fast, sell smart, and scale with confidence.
                </p>
                <h6 ref={addToTextRefs} className='text-[#000000] font-[700] min-[950px]:leading-[10px] mt-3'>
                    Where are you In your Journey?
                </h6>
                
                <ol className='flex flex-col gap-y-1'>
                    <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                        <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                        <p className='text-[#374151] text-[15px] font-[600]'>Idea Stage → Get MVP built with us</p>
                    </li>
                    <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                        <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                        <p className='text-[#374151] text-[15px] font-[600]'>MVP Ready → Start customer acquisition</p>
                    </li>
                    <li ref={addToListRefs} className='grid grid-cols-[max-content_1fr] gap-x-3 items-center'>
                        <span className='bg-[#EAB308] rounded-full p-1'><FaArrowRight className='text-white text-[10px]' /></span>
                        <p className='text-[#374151] text-[15px] font-[600]'>Early Revenue → Get investor-ready and scale</p>
                    </li>
                </ol>
            </div>

            {/* --- RIGHT COLUMN --- */}
            <div className='h-full grid grid-cols-1 sm:grid-cols-2 min-[1028px]:grid-cols-3 gap-3'>
                {/* CARD 1 */}
                <div 
                    ref={addToCardRefs}
                    className='h-full bg-gradient-to-b from-[#02C6F6] via-[#182186] to-[#181F85] rounded-[20px] relative overflow-hidden grid grid-rows-[1fr_max-content] gap-y-3 transform hover:scale-[1.02] transition-transform duration-300'
                >
                    <img src='img/how-we-do-it-1.png' className='w-full h-full object-cover opacity-[0.18] absolute top-0 left-0' alt="Step 1" />
                    <div className='p-5 flex z-10'>
                        <span className='flex items-center justify-center w-10 h-10 rounded-full bg-[#FFFFFF] text-[#0D3BD1] font-[700] text-[20px]'>1</span>
                    </div>
                    <div className='px-4 py-8 flex flex-col gap-y-8'>
                        <span className='z-10 text-[#FAFCFF] text-center font-medium'>We validate your idea and build the MVP.</span>
                        <a href='https://forms.gle/Brpzz6dAtLtcTYc9A' target="_blank" rel="noreferrer" className='bg-[#41D8FF] hover:bg-[#03a3cc] rounded-full px-3 py-1.5 text-[#000000] font-[600] text-[16px] z-10 text-center transition-colors'>Co-Build</a>
                    </div>
                </div>

                {/* CARD 2 */}
                <div 
                    ref={addToCardRefs}
                    className='h-full bg-gradient-to-b from-[#02C6F6] via-[#182186] to-[#181F85] rounded-[20px] relative overflow-hidden grid grid-rows-[1fr_max-content] gap-y-3 transform hover:scale-[1.02] transition-transform duration-300'
                >
                    <img src='img/how-we-do-it-2.png' className='w-full h-full object-cover opacity-[0.18] absolute top-0 left-0' alt="Step 2" />
                    <div className='p-5 flex z-10'>
                        <span className='flex items-center justify-center w-10 h-10 rounded-full bg-[#FFFFFF] text-[#0D3BD1] font-[700] text-[20px]'>2</span>
                    </div>
                    <div className='px-4 py-8 flex flex-col gap-y-8'>
                        <span className='z-10 text-[#FAFCFF] text-center font-medium'>We acquire early users and create sales traction.</span>
                        <a href='https://forms.gle/Brpzz6dAtLtcTYc9A' target="_blank" rel="noreferrer" className='bg-[#41D8FF] hover:bg-[#03a3cc] rounded-full px-3 py-1.5 text-[#000000] font-[600] text-[16px] z-10 text-center transition-colors'>Go-to-Market</a>
                    </div>
                </div>

                {/* CARD 3 */}
                <div 
                    ref={addToCardRefs}
                    className='max-[1028px]:col-span-full h-full bg-gradient-to-b from-[#02C6F6] via-[#182186] to-[#181F85] rounded-[20px] relative overflow-hidden grid grid-rows-[1fr_max-content] gap-y-3 transform hover:scale-[1.02] transition-transform duration-300'
                >
                    <img src='img/how-we-do-it-3.png' className='w-full h-full object-cover opacity-[0.18] absolute top-0 left-0' alt="Step 3" />
                    <div className='p-5 flex z-10'>
                        <span className='flex items-center justify-center w-10 h-10 rounded-full bg-[#FFFFFF] text-[#0D3BD1] font-[700] text-[20px]'>3</span>
                    </div>
                    <div className='px-4 py-8 flex flex-col gap-y-8'>
                        <span className='z-10 text-[#FAFCFF] text-center font-medium'>Access capital, partners, and scale in India & Asia</span>
                        <a href='https://forms.gle/Brpzz6dAtLtcTYc9A' target="_blank" rel="noreferrer" className='bg-[#41D8FF] hover:bg-[#03a3cc] rounded-full px-3 py-1.5 text-[#000000] font-[600] text-[16px] z-10 text-center transition-colors'>Scale & Fundraise</a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowWeDoItSection;